import React from "react";

import { observer } from "mobx-react-lite";

import type { FileMetaDto } from "../../api";
import { cn } from "@synthesis/shared-utils";

import { ExplorerView, type ExplorerViewValue, useStores } from "../../store";
import { Files } from "../files";
import { Folders } from "../folders";
import { ModalCreate } from "./local-components";

type MainPageProps = {
  model?: boolean;
  disabledRight?: boolean;
  isIntegration?: boolean;
  openPdfModal?: (file: FileMetaDto) => void;
};

const MainPage: React.FC<MainPageProps> = observer(({ disabledRight, isIntegration, openPdfModal }) => {
  const { generalStore, directoryStore } = useStores();
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (directoryStore.currentFolder && containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [directoryStore.currentFolder]);

  React.useEffect(() => {
    generalStore.historyReset();
  }, [generalStore]);

  React.useEffect(
    function handleEscButton() {
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          generalStore.resetCheckedAll();
        }
      };

      window.addEventListener("keydown", handleEsc);
      return () => {
        window.removeEventListener("keydown", handleEsc);
      };
    },
    [generalStore],
  );

  const closeModalCreate = () => {
    setIsOpen(false);
  };

  const ContainerRender = (
    <Container data-cy="explorer-container-projectDocks" view={generalStore.view} id="contextMenuExplorer">
      <Folders id="contextMenuExplorer" />
      <Files id="contextMenuExplorer" disabledRight={disabledRight} isIntegration={isIntegration} openPdfModal={openPdfModal} />
    </Container>
  );

  return (
    <MainBlock
      data-cy="explorer-mainBlock-projectDocks"
      ref={containerRef}
      notTable={generalStore.view !== ExplorerView.Table}
      id="contextMenuExplorer"
    >
      {ContainerRender}
      {isOpen && <ModalCreate isOpen={isOpen} setIsOpen={setIsOpen} openModal={() => setIsOpen(true)} closeModal={closeModalCreate} />}
    </MainBlock>
  );
});

const scrollableMainClassName =
  "w-full [&::-webkit-scrollbar]:w-[7px] [&::-webkit-scrollbar]:rounded-lg [&::-webkit-scrollbar-track]:rounded-lg [&::-webkit-scrollbar-track]:bg-white [&::-webkit-scrollbar-thumb]:rounded-[20px] [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-white [&::-webkit-scrollbar-thumb]:bg-[#d1d5db]";

type ExplorerContainerProps = React.HTMLAttributes<HTMLDivElement> & { view: ExplorerViewValue };

const Container = ({ view, className, ...props }: ExplorerContainerProps) => (
  <div
    className={cn(
      "flex",
      view === ExplorerView.Grid && "flex-row flex-wrap gap-4",
      view === ExplorerView.List && "flex-col",
      view === ExplorerView.Table && "h-full flex-col",
      className,
    )}
    {...props}
  />
);

type MainBlockProps = React.HTMLAttributes<HTMLElement> & { notTable: boolean };

const MainBlock = React.forwardRef<HTMLElement, MainBlockProps>(({ notTable, className, ...props }, ref) => (
  <main
    ref={ref}
    className={cn(scrollableMainClassName, notTable ? "h-[calc(100%-26px)] overflow-y-scroll pb-2" : "h-full min-h-0", className)}
    {...props}
  />
));

export default MainPage;
